import {ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';

/** PANEL_DATA of the select panels built on this list. */
export interface TimecardSelectPanelData {
  selectedId?: string;
}

export interface SearchSelectItem<T> {
  id: string;
  label: string;
  value: T;
}

export interface SearchSelectPage<T> {
  cursor: string;
  list: SearchSelectItem<T>[];
}

export interface SearchSelectPageParams {
  limit: number;
  query: string;
  sort: string;
  cursor?: string;
}

export type SearchSelectFetchPage<T> = (params: SearchSelectPageParams) => Observable<SearchSelectPage<T>>;

const LOAD_LIMIT = 20;
const SCROLL_THRESHOLD_PX = 40;

/**
 * Server-side search with paging, for lists a site can have thousands of. What is searched comes from
 * `fetchPage`, so the panels around it only say where the items come from and how they are labelled.
 */
require('./timecard-search-select-list.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-search-select-list',
  templateUrl: './timecard-search-select-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardSearchSelectListComponent<T> implements OnInit {

  readonly fetchPage = input.required<SearchSelectFetchPage<T>>();
  readonly selectedId = input<string>();
  // Translation keys
  readonly searchLabel = input.required<string>();
  readonly emptyLabel = input.required<string>();
  readonly listLabel = input.required<string>();

  readonly selected = output<T>();

  readonly queryString = signal('');
  readonly items = signal<SearchSelectItem<T>[]>([]);
  readonly isLoading = signal(true);
  readonly isInitialLoading = signal(true);
  readonly isEmpty = computed(() => !this.isLoading() && !this.items().length);

  private cursor: string = null;
  private readonly reload$ = new Subject<void>();
  private readonly fetchNext$ = new Subject<string>();

  constructor() {
    this.reload$.pipe(
      switchMap(() => this.loadPage().pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.items.set(response.list);
      this.setLoaded();
    });

    this.fetchNext$.pipe(
      switchMap((cursor) => this.loadPage(cursor).pipe(
        catchError(() => {
          this.setLoaded();
          return EMPTY;
        }),
        // A new search replaces the list, so a page still in flight must not be appended to it.
        takeUntil(this.reload$)
      )),
      takeUntilDestroyed()
    ).subscribe((response) => {
      this.cursor = response.cursor;
      this.items.update((items) => items.concat(response.list));
      this.setLoaded();
    });
  }

  // Not in the constructor: fetchPage is an input, so it is only set by now.
  ngOnInit() {
    this.reload();
  }

  onQueryChange(query: string) {
    this.queryString.set(query || '');
    this.reload();
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_THRESHOLD_PX;
    if (isNearBottom && this.cursor && !this.isLoading()) {
      this.isLoading.set(true);
      this.fetchNext$.next(this.cursor);
    }
  }

  onSelectId(id: unknown) {
    const item = _.find(this.items(), {id: id as string});
    if (item) {
      this.selected.emit(item.value);
    }
  }

  private reload() {
    this.cursor = null;
    this.isLoading.set(true);
    this.isInitialLoading.set(true);
    this.reload$.next();
  }

  private setLoaded() {
    this.isLoading.set(false);
    this.isInitialLoading.set(false);
  }

  private loadPage(cursor?: string): Observable<SearchSelectPage<T>> {
    return this.fetchPage()({limit: LOAD_LIMIT, query: this.queryString(), sort: 'name,asc', ...(cursor ? {cursor} : {})});
  }
}
